import { useCallback } from 'react';
import { notificationService } from '@/lib/services/notification.service';
import { getWidgetStores } from '@/entities/widget';
import { logger } from '@/lib/utils/logger';
import type { UseEmailActionsReturn } from './useEmailActions.types';
import { useTranslation } from 'react-i18next';

export const useEmailActions = (htmlContent: string): UseEmailActionsReturn => {
  const { t } = useTranslation('contentTest');

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`.
  const { widgetStore, uiStore } = getWidgetStores();
  const { closeSidePanel } = uiStore.useSidePanel();
  const { clearWidget, widget } = widgetStore.useWidgetPanel();
  const clearActiveVersion = widgetStore.useWidgetStore(
    (state) => state.clearActiveVersion as (agentId: string) => void,
  );

  const handleCopyHtml = useCallback(async () => {
    if (!htmlContent) {
      return;
    }
    try {
      await navigator.clipboard.writeText(htmlContent);
      notificationService.showSuccess(t('emailFlowWidget.useEmailActions.emailTemplateCopied'));
    } catch (fallbackErr) {
      logger.error('Fallback copy failed:', fallbackErr);
      notificationService.showError(t('emailFlowWidget.useEmailActions.failedToCopyEmailTemplate'));
    }
  }, [htmlContent, t]);

  const handleExportHtml = useCallback(() => {
    if (!htmlContent) {
      return;
    }

    try {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email-template.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notificationService.showSuccess(t('emailFlowWidget.useEmailActions.emailTemplateDownloaded'));
    } catch (err) {
      logger.error('Failed to export HTML content:', err);
      notificationService.showError(
        t('emailFlowWidget.useEmailActions.failedToDownloadEmailTemplate'),
      );
    }
  }, [htmlContent, t]);

  const handleClose = useCallback(() => {
    clearWidget();
    if (widget?.agentName) {
      clearActiveVersion(widget.agentName);
    }
    closeSidePanel();
  }, [clearWidget, widget, clearActiveVersion, closeSidePanel]);

  return {
    handleCopyHtml,
    handleExportHtml,
    handleClose,
  };
};
